import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaGavel,
  FaSyncAlt,
  FaTimesCircle,
  FaArrowLeft,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaTag,
  FaList,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

const LOT_STATUSES = ['all', 'sold', 'unsold', 'active'] as const;

type TabType = 'info' | 'lots';

const AuctionDetails: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const navigate = useNavigate();

  const [auction, setAuction] = useState<any>(null);
  const [lots, setLots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLots, setLoadingLots] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [lotStatusFilter, setLotStatusFilter] = useState<string>('all');

  const loadAuction = useCallback(async () => {
    if (!auctionId) return;
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAuctionById(auctionId);
      setAuction(data);
    } catch (err: any) {
      console.error('Error loading auction:', err);
      setError(err?.message || 'Failed to load auction details');
      toast.error(err?.message || 'Failed to load auction details');
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  const loadLots = useCallback(async () => {
    if (!auctionId) return;
    try {
      setLoadingLots(true);
      const filters: { status?: string } = {};
      if (lotStatusFilter && lotStatusFilter !== 'all') filters.status = lotStatusFilter;

      const data = await apiService.getAuctionLots(auctionId, filters);
      const lotList = Array.isArray(data) ? data : data.lots || data.data || [];
      setLots(lotList);
    } catch (err: any) {
      console.error('Error loading lots:', err);
      toast.error(err?.message || 'Failed to load auction lots');
    } finally {
      setLoadingLots(false);
    }
  }, [auctionId, lotStatusFilter]);

  useEffect(() => {
    loadAuction();
  }, [loadAuction]);

  useEffect(() => {
    if (activeTab === 'lots') {
      loadLots();
    }
  }, [activeTab, loadLots]);

  const getStateColor = (state: string) => {
    switch (state?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 dark:bg-success-500/20 text-emerald-700 dark:text-success-400 border-emerald-200 dark:border-success-500/30';
      case 'waiting':
        return 'bg-amber-100 dark:bg-warning-500/20 text-amber-700 dark:text-warning-400 border-amber-200 dark:border-warning-500/30';
      case 'paused':
        return 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30';
      case 'completed':
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
      default:
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  const getLotStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'sold':
        return 'bg-emerald-100 dark:bg-success-500/20 text-emerald-700 dark:text-success-400 border-emerald-200 dark:border-success-500/30';
      case 'unsold':
        return 'bg-red-100 dark:bg-danger-500/20 text-red-700 dark:text-danger-400 border-red-200 dark:border-danger-500/30';
      case 'active':
        return 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30';
      default:
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  const tabs = [
    { id: 'info' as TabType, label: 'Auction Info', icon: FaGavel },
    { id: 'lots' as TabType, label: 'Lots', icon: FaList, count: lots.length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading auction details...</p>
        </div>
      </div>
    );
  }

  if (error && !auction) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/dashboard/auctions')}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <FaArrowLeft className="h-4 w-4 mr-2" />
          Back to Auctions
        </button>
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/auctions')}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-400/30 transition-colors"
          >
            <FaArrowLeft className="h-5 w-5" />
          </button>
          <div className="p-3 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg border border-indigo-200 dark:border-indigo-500/30">
            <FaGavel className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {auction?.name || auction?.title || 'Auction Details'}
              </h1>
              {auction?.state && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStateColor(auction.state)}`}>
                  {auction.state}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {auction?.seriesName || ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => { loadAuction(); if (activeTab === 'lots') loadLots(); }}
          disabled={loading || loadingLots}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
        >
          <FaSyncAlt className={`h-4 w-4 mr-2 ${loading || loadingLots ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-dark-300/40">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-all relative ${
                  isActive
                    ? 'text-indigo-700 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.count != null && (
                  <span className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${
                    isActive
                      ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400'
                      : 'bg-gray-100 dark:bg-dark-300/50 text-gray-500 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Info Tab */}
          {activeTab === 'info' && auction && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {auction.totalLots != null && (
                  <div className="text-center p-4 bg-gray-50 dark:bg-dark-300/30 rounded-lg">
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">{auction.totalLots}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Lots</p>
                  </div>
                )}
                {auction.soldLots != null && (
                  <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{auction.soldLots}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Sold</p>
                  </div>
                )}
                {auction.unsoldLots != null && (
                  <div className="text-center p-4 bg-red-50 dark:bg-red-500/10 rounded-lg">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400">{auction.unsoldLots}</p>
                    <p className="text-xs text-red-600 dark:text-red-500 mt-1">Unsold</p>
                  </div>
                )}
                {(auction.participantCount != null || auction.teamCount != null) && (
                  <div className="text-center p-4 bg-blue-50 dark:bg-primary-500/10 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700 dark:text-primary-400">{auction.participantCount ?? auction.teamCount ?? 0}</p>
                    <p className="text-xs text-blue-600 dark:text-primary-500 mt-1">Participants</p>
                  </div>
                )}
              </div>

              {/* Auction Details Table */}
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-300/40">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                  <tbody className="divide-y divide-gray-200 dark:divide-dark-300/40">
                    {auction.state && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30 w-1/3">Session State</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStateColor(auction.state)}`}>
                            {auction.state}
                          </span>
                        </td>
                      </tr>
                    )}
                    {auction.seriesName && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">Series</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{auction.seriesName}</td>
                      </tr>
                    )}
                    {auction.currentLot && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">Current Lot</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {auction.currentLot.playerName || auction.currentLot.name || JSON.stringify(auction.currentLot)}
                        </td>
                      </tr>
                    )}
                    {auction.currentBid != null && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">Current Bid</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(auction.currentBid)}</td>
                      </tr>
                    )}
                    {auction.startTime && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">Start Time</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white flex items-center gap-2">
                          <FaClock className="h-3.5 w-3.5 text-gray-400" />
                          {new Date(auction.startTime).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {auction.endTime && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">End Time</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white flex items-center gap-2">
                          <FaClock className="h-3.5 w-3.5 text-gray-400" />
                          {new Date(auction.endTime).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {auction.createdAt && (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-300/30">Created</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(auction.createdAt).toLocaleString()}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Teams/Participants */}
              {auction.teams && auction.teams.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <FaUsers className="h-4 w-4" />
                    Participants ({auction.teams.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {auction.teams.map((team: any, idx: number) => (
                      <span
                        key={team.id || team._id || idx}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border border-blue-200 dark:border-primary-500/30"
                      >
                        {team.name || team.teamName || `Team ${idx + 1}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lots Tab */}
          {activeTab === 'lots' && (
            <div className="space-y-4">
              {/* Lot Filter */}
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                  <select
                    value={lotStatusFilter}
                    onChange={(e) => setLotStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-dark-500 border border-gray-300 dark:border-dark-300 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {LOT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                {loadingLots && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <FaSyncAlt className="h-3.5 w-3.5 animate-spin" />
                    Loading...
                  </div>
                )}
              </div>

              {/* Lots Grid */}
              {lots.length === 0 && !loadingLots ? (
                <div className="text-center py-12">
                  <FaTag className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Lots Found</h3>
                  <p className="text-gray-600 dark:text-gray-400">No lots match the current filter.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lots.map((lot: any, idx: number) => (
                    <div
                      key={lot.id || lot._id || idx}
                      className="bg-gray-50 dark:bg-dark-300/30 rounded-xl border border-gray-200 dark:border-dark-300/40 p-4 transition-all hover:shadow-md"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                          {lot.playerName || lot.name || `Lot #${lot.lotNumber || idx + 1}`}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getLotStatusColor(lot.status)}`}>
                          {lot.status || 'pending'}
                        </span>
                      </div>

                      {lot.role && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{lot.role}</p>
                      )}

                      <div className="space-y-1.5">
                        {lot.basePrice != null && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Base Price</span>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(lot.basePrice)}</span>
                          </div>
                        )}
                        {lot.soldPrice != null && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Sold Price</span>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(lot.soldPrice)}</span>
                          </div>
                        )}
                        {(lot.winningTeam || lot.winningTeamName || lot.soldTo) && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Won By</span>
                            <span className="font-semibold text-blue-600 dark:text-primary-400">
                              {lot.winningTeamName || lot.winningTeam || lot.soldTo}
                            </span>
                          </div>
                        )}
                      </div>

                      {lot.status?.toLowerCase() === 'sold' && (
                        <div className="mt-3 flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                          <FaCheckCircle className="h-3 w-3" />
                          <span>Sold</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionDetails;
